import { PresentationTimeFrameConversionService } from '@src/services/timeline/PresentationTimeFrameConversionService';
import { useCallback, useMemo, useState } from 'react';

import type { ElementState } from '@src/renderer/ElementState';
import type { IVideoCreatorStore } from '@src/stores/IVideoCreatorStore';
import type React from 'react';

interface UseTimelineNavigationParams {
  videoCreator: IVideoCreatorStore;
  scale: number;
  frameRate: number;
  duration: number;
  tracks: Array<[number, ElementState[]]>;
  activeTracks: number[];
  timelineRef: React.RefObject<HTMLDivElement>;
}

interface NavigateOptions {
  manageState?: boolean;
}

export interface TimelineNavigationInterval {
  track: number;
  start: number;
  end: number;
}

export interface TimelineGapRange {
  start: number;
  end: number;
}

export function buildTimelineGapRanges(
  selectedIntervals: TimelineNavigationInterval[],
  duration: number,
  gapThreshold: number,
): TimelineGapRange[] {
  const safeDuration = Number.isFinite(duration) ? Math.max(duration, 0) : 0;
  const safeGapThreshold =
    Number.isFinite(gapThreshold) && gapThreshold > 0 ? gapThreshold : 0;
  if (safeDuration <= 0) return [];

  const finiteIntervals = selectedIntervals.filter(
    interval =>
      Number.isFinite(interval.start) && Number.isFinite(interval.end),
  );
  if (finiteIntervals.length === 0) {
    return safeDuration >= safeGapThreshold
      ? [{ start: 0, end: safeDuration }]
      : [];
  }

  const events = finiteIntervals
    .flatMap(({ start, end }) => [
      { time: Math.min(Math.max(start, 0), safeDuration), delta: 1 },
      { time: Math.min(Math.max(end, 0), safeDuration), delta: -1 },
    ])
    .sort((a, b) => (a.time === b.time ? b.delta - a.delta : a.time - b.time));

  const ranges: TimelineGapRange[] = [];
  let active = 0;
  let previousTime = 0;
  let index = 0;

  const recordGap = (start: number, end: number) => {
    if (end - start >= safeGapThreshold) {
      ranges.push({ start, end });
    }
  };

  while (index < events.length) {
    const eventTime = events[index].time;
    if (eventTime > previousTime && active === 0) {
      recordGap(previousTime, eventTime);
    }

    // A do/while guarantees forward progress even if future callers regress
    // the finite-value filter above. The old equality-only loop stalled
    // forever on NaN because NaN !== NaN.
    do {
      active = Math.max(0, active + events[index].delta);
      index += 1;
    } while (index < events.length && events[index].time === eventTime);

    previousTime = eventTime;
  }

  if (previousTime < safeDuration && active === 0) {
    recordGap(previousTime, safeDuration);
  }

  return ranges;
}

export const useTimelineNavigation = ({
  videoCreator,
  scale,
  frameRate,
  duration,
  tracks,
  activeTracks,
  timelineRef,
}: UseTimelineNavigationParams) => {
  const [isNavigating, setIsNavigating] = useState(false);

  const timeFrameConversionService = useMemo(
    () => new PresentationTimeFrameConversionService(),
    [],
  );

  const safeFrameRate = frameRate > 0 ? frameRate : 30;
  const frameDuration = 1 / safeFrameRate;
  const gapThreshold = frameDuration;
  const epsilon = frameDuration / 2;
  const clampTime = useCallback(
    (value: number) => {
      const safeDuration = Number.isFinite(duration)
        ? Math.max(duration, 0)
        : 0;
      if (!Number.isFinite(value)) return 0;
      return Math.min(Math.max(value, 0), safeDuration);
    },
    [duration],
  );

  const selectedTrackEntries = useMemo(() => {
    if (activeTracks.length > 0) {
      return tracks.filter(([trackNumber]) =>
        activeTracks.includes(trackNumber),
      );
    }

    const fallbackTrack = tracks.find(([, elements]) =>
      elements.some(element =>
        videoCreator.isOriginalVideoElement(element.source),
      ),
    );

    return fallbackTrack ? [fallbackTrack] : [];
  }, [tracks, activeTracks, videoCreator]);

  const selectedIntervals = useMemo(() => {
    return selectedTrackEntries.flatMap(([trackNumber, elements]) =>
      elements.map(element => {
        const baseStart = element.globalTime ?? 0;
        const start = clampTime(baseStart);
        const elementDuration = Number.isFinite(element.duration)
          ? element.duration
          : 0;
        const rawEnd = baseStart + elementDuration;
        const end = clampTime(rawEnd);
        return {
          track: trackNumber,
          start,
          end: end < start ? start : end,
        };
      }),
    );
  }, [selectedTrackEntries, clampTime]);

  const intervalBoundaries = useMemo(() => {
    const times = new Set<number>();
    times.add(0);
    times.add(clampTime(duration));
    selectedIntervals.forEach(interval => {
      times.add(interval.start);
      times.add(interval.end);
    });

    return Array.from(times)
      .filter(time => Number.isFinite(time))
      .sort((a, b) => a - b);
  }, [selectedIntervals, duration, clampTime]);

  const gapRanges = useMemo(() => {
    return buildTimelineGapRanges(selectedIntervals, duration, gapThreshold);
  }, [selectedIntervals, duration, gapThreshold]);

  const editPoints = useMemo(() => {
    const times = new Set<number>(intervalBoundaries);
    gapRanges.forEach(range => {
      times.add(range.start);
      times.add(range.end);
    });

    return Array.from(times)
      .filter(time => Number.isFinite(time))
      .sort((a, b) => a - b);
  }, [intervalBoundaries, gapRanges]);

  const findNextEditPoint = useCallback(
    (time: number) => {
      for (let i = 0; i < editPoints.length; i += 1) {
        if (editPoints[i] > time + epsilon) {
          return editPoints[i];
        }
      }
      return undefined;
    },
    [editPoints, epsilon],
  );

  const findPreviousEditPoint = useCallback(
    (time: number) => {
      for (let i = editPoints.length - 1; i >= 0; i -= 1) {
        if (editPoints[i] < time - epsilon) {
          return editPoints[i];
        }
      }
      return undefined;
    },
    [editPoints, epsilon],
  );

  const navigateToTime = useCallback(
    async (time: number, options?: NavigateOptions) => {
      const shouldManageState = options?.manageState ?? true;
      if (shouldManageState) {
        setIsNavigating(true);
      }
      await videoCreator.setTime(time, true, false);

      if (shouldManageState) {
        setIsNavigating(false);
      }
    },
    [videoCreator],
  );

  const navigateByFrames = useCallback(
    async (deltaFrames: number) => {
      if (!Number.isFinite(frameRate) || frameRate <= 0) {
        return;
      }

      const currentFrame = Math.round(videoCreator.time * frameRate);
      const targetFrame = Math.max(0, currentFrame + deltaFrames);
      const baseTime = targetFrame / frameRate;
      const targetTime = timeFrameConversionService.adjustTimeForNavigation(
        baseTime,
        frameRate,
      );
      await navigateToTime(targetTime);
    },
    [frameRate, navigateToTime, videoCreator, timeFrameConversionService],
  );

  const navigateToNextEditPoint = useCallback(async () => {
    const target = findNextEditPoint(videoCreator.time);
    if (typeof target === 'number') {
      await navigateToTime(target);
    }
  }, [findNextEditPoint, navigateToTime, videoCreator]);

  const navigateToPreviousEditPoint = useCallback(async () => {
    const target = findPreviousEditPoint(videoCreator.time);
    if (typeof target === 'number') {
      await navigateToTime(target);
    }
  }, [findPreviousEditPoint, navigateToTime, videoCreator]);

  const navigateToNextFrame = useCallback(async () => {
    await navigateByFrames(1);
  }, [navigateByFrames]);

  const navigateToPreviousFrame = useCallback(async () => {
    await navigateByFrames(-1);
  }, [navigateByFrames]);

  const scrubToTime = useCallback(
    (time: number) => {
      const targetTime = timeFrameConversionService.adjustTimeForNavigation(
        time,
        frameRate,
      );

      return navigateToTime(targetTime, { manageState: false });
    },
    [navigateToTime, frameRate, timeFrameConversionService],
  );

  const handleRepositionPlayheadClick = useCallback(
    async (
      e: React.MouseEvent<HTMLDivElement>,
      tracksDivRef: React.RefObject<HTMLDivElement>,
    ) => {
      setIsNavigating(true);

      try {
        const isPlaying = videoCreator.isPlaying;
        const offsetX = timelineRef.current?.scrollLeft || 0;

        if (
          !videoCreator.isPlayheadDragging &&
          !videoCreator.isScrubbing &&
          tracksDivRef.current
        ) {
          const elementLeftBoundary =
            tracksDivRef.current.getBoundingClientRect().left;
          const clickPositionRelativeToElement =
            e.clientX - elementLeftBoundary;
          const clickX = clickPositionRelativeToElement + offsetX;

          const time = (clickX - 14) / scale;
          await navigateToTime(time, { manageState: false });

          if (isPlaying) {
            videoCreator.renderer?.play();
          }
        }
      } finally {
        setIsNavigating(false);
      }
    },
    [navigateToTime, videoCreator, timelineRef, scale],
  );

  return {
    isNavigating,
    navigateToTime,
    navigateToNextFrame,
    navigateToPreviousFrame,
    navigateToNextEditPoint,
    navigateToPreviousEditPoint,
    scrubToTime,
    handleRepositionPlayheadClick,
  };
};
